import { images } from '../images';
import type { CancellationPolicy, HotelContent, HotelSummary, Order, Rate, Tax, UserProfile } from './types';

// The first four demo hotels are real properties, named and photographed
// exactly as the SuiteScanner marketing site presents them — never pair one
// of those photos with a different hotel. The final three properties come
// from the supplied discovery reference and intentionally use neutral room
// placeholders until their approved photography is available. The Phuket Marriott nightly prices
// come from the site's published example (Booking.com $999 vs $651
// wholesale for 5 nights), rounded to per-night figures.
// Review scores mirror the shape of RateHawk's separately synchronized
// reviews feed and are demo values only.

function taxes(cityTax: string, serviceFee: string): Tax[] {
  return [
    { name: 'city_tax', amount: cityTax, currencyCode: 'USD', includedBySupplier: false },
    { name: 'service_fee', amount: serviceFee, currencyCode: 'USD', includedBySupplier: false },
  ];
}

function nonRefundable(): CancellationPolicy {
  return { freeCancellationBefore: null, tiers: [] };
}

function freeCancellationUntil(iso: string, charge: string): CancellationPolicy {
  return {
    freeCancellationBefore: iso,
    tiers: [
      { startAt: null, endAt: iso, amountCharge: { amount: '0.00', currencyCode: 'USD' } },
      { startAt: iso, endAt: null, amountCharge: { amount: charge, currencyCode: 'USD' } },
    ],
  };
}

function rate(overrides: Partial<Rate> & Pick<Rate, 'bookHash' | 'roomName' | 'price' | 'images'>): Rate {
  return {
    meal: { value: 'nomeal', hasBreakfast: false },
    taxes: taxes('12.40', '3.70'),
    cancellation: nonRefundable(),
    paymentType: 'now',
    isNeedCreditCardData: true,
    ...overrides,
  };
}

export const mockHotels: HotelSummary[] = [
  {
    id: 'phuket_marriott_merlin_beach',
    hid: 1001,
    name: 'Phuket Marriott Resort & Spa, Merlin Beach',
    starRating: 5,
    reviewScore: 8.8,
    reviewCount: 2876,
    thumbnail: images.phuketMarriott,
    address: 'Tri-Trang Beach, Patong, Phuket, Thailand',
    lowestPrice: { amount: '130.00', currencyCode: 'USD' },
    retailPrice: { amount: '200.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-marriott-deluxe-pool',
        roomName: 'Deluxe Room, Pool View',
        price: { amount: '130.00', currencyCode: 'USD' },
        cancellation: freeCancellationUntil('2026-08-25T17:00:00Z', '130.00'),
        images: [images.roomBedroom, images.roomTerrace],
      }),
      rate({
        bookHash: 'h-marriott-deluxe-terrace',
        roomName: 'Deluxe Room, Terrace',
        price: { amount: '148.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        cancellation: freeCancellationUntil('2026-08-25T17:00:00Z', '148.00'),
        images: [images.roomTerrace, images.roomBedroom],
      }),
    ],
  },
  {
    id: 'outrigger_maldives_maafushivaru',
    hid: 1002,
    name: 'Outrigger Maldives Maafushivaru Resort',
    starRating: 5,
    reviewScore: 9.4,
    reviewCount: 1204,
    thumbnail: images.outriggerMaldives,
    address: 'Maafushivaru Island, South Ari Atoll, Maldives',
    lowestPrice: { amount: '412.00', currencyCode: 'USD' },
    retailPrice: { amount: '560.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-outrigger-beach-villa',
        roomName: 'Beach Villa',
        price: { amount: '412.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        cancellation: freeCancellationUntil('2026-09-15T12:00:00Z', '412.00'),
        images: [images.roomBalconyCoast, images.roomBedroom],
      }),
    ],
  },
  {
    id: 'wafaifo_resort_hoi_an',
    hid: 1003,
    name: 'Wafaifo Resort, Hoi An',
    starRating: 4,
    reviewScore: 8.6,
    reviewCount: 942,
    thumbnail: images.wafaifoHoiAn,
    address: 'Hung Vuong Street, Hoi An, Vietnam',
    lowestPrice: { amount: '86.00', currencyCode: 'USD' },
    retailPrice: { amount: '129.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-wafaifo-premier',
        roomName: 'Premier Double Room',
        price: { amount: '86.00', currencyCode: 'USD' },
        paymentType: 'hotel',
        isNeedCreditCardData: false,
        images: [images.roomBedroom, images.roomCorridor],
      }),
    ],
  },
  {
    id: 'the_peninsula_bangkok',
    hid: 1004,
    name: 'The Peninsula Bangkok',
    starRating: 5,
    reviewScore: 9.1,
    reviewCount: 3510,
    thumbnail: images.peninsulaBangkok,
    address: '333 Charoen Nakhon Rd, Bangkok, Thailand',
    lowestPrice: { amount: '245.00', currencyCode: 'USD' },
    retailPrice: { amount: '349.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-peninsula-bkk-river',
        roomName: 'Deluxe Room, River View',
        price: { amount: '245.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        paymentType: 'deposit',
        cancellation: freeCancellationUntil('2026-11-01T20:00:00Z', '245.00'),
        images: [images.roomBedroom, images.roomBalconyCoast],
      }),
    ],
  },
  {
    id: 'siam_kempinski_bangkok',
    hid: 1005,
    name: 'Siam Kempinski Hotel Bangkok',
    starRating: 5,
    reviewScore: 9.5,
    reviewCount: 2187,
    thumbnail: images.roomBedroom,
    address: '991/9 Rama I Road, Bangkok, Thailand',
    lowestPrice: { amount: '649.00', currencyCode: 'USD' },
    retailPrice: { amount: '689.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-kempinski-deluxe',
        roomName: 'Deluxe Room',
        price: { amount: '649.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        cancellation: freeCancellationUntil('2026-10-20T17:00:00Z', '649.00'),
        images: [images.roomBedroom, images.roomTerrace],
      }),
    ],
  },
  {
    id: 'shangri_la_bangkok',
    hid: 1006,
    name: 'Shangri-La Bangkok',
    starRating: 5,
    reviewScore: 9.0,
    reviewCount: 4065,
    thumbnail: images.roomTerrace,
    address: '89 Soi Wat Suan Plu, Bangkok, Thailand',
    lowestPrice: { amount: '449.00', currencyCode: 'USD' },
    retailPrice: { amount: '539.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-shangrila-river',
        roomName: 'Deluxe River View',
        price: { amount: '449.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        cancellation: freeCancellationUntil('2026-10-20T17:00:00Z', '449.00'),
        images: [images.roomTerrace, images.roomBedroom],
      }),
    ],
  },
  {
    id: 'amari_bangkok',
    hid: 1007,
    name: 'Amari Bangkok',
    starRating: 5,
    reviewScore: 8.7,
    reviewCount: 1633,
    thumbnail: images.roomBalconyCoast,
    address: '847 Phetchaburi Road, Bangkok, Thailand',
    lowestPrice: { amount: '229.00', currencyCode: 'USD' },
    retailPrice: { amount: '282.00', currencyCode: 'USD' },
    rates: [
      rate({
        bookHash: 'h-amari-premier',
        roomName: 'Premier Room',
        price: { amount: '229.00', currencyCode: 'USD' },
        meal: { value: 'breakfast', hasBreakfast: true },
        cancellation: freeCancellationUntil('2026-10-20T17:00:00Z', '229.00'),
        images: [images.roomBalconyCoast, images.roomBedroom],
      }),
    ],
  },
];

const HOTEL_DESCRIPTIONS: Record<string, string> = {
  phuket_marriott_merlin_beach:
    'A beachfront resort wrapped around three pools on the quiet side of Patong, ' +
    'with direct access to Tri-Trang Beach and tropical gardens throughout.',
  outrigger_maldives_maafushivaru:
    'A private-island resort in the South Ari Atoll with its own house reef, ' +
    'overwater and beachfront villas, and dining over the lagoon.',
  wafaifo_resort_hoi_an:
    'A resort built around a long central pool, minutes from Hoi An Ancient Town, ' +
    'with cabana-lined decks and balconies over the water.',
  the_peninsula_bangkok:
    'A riverside classic on the Chao Phraya with landscaped gardens, ' +
    'river-view rooms and the Peninsula’s signature service.',
  siam_kempinski_bangkok:
    'A central Bangkok stay with resort-style gardens, pools and direct access to the Siam shopping district.',
  shangri_la_bangkok:
    'A Chao Phraya riverfront stay with sweeping water views, garden pools and easy access to the city.',
  amari_bangkok:
    'A modern central Bangkok hotel near Pratunam with spacious rooms, skyline views and a rooftop pool.',
};

const HOTEL_GALLERIES: Record<string, HotelContent['images']> = {
  phuket_marriott_merlin_beach: [images.phuketMarriott, images.roomBedroom, images.roomTerrace],
  outrigger_maldives_maafushivaru: [images.outriggerMaldives, images.roomBalconyCoast, images.roomBedroom],
  wafaifo_resort_hoi_an: [images.wafaifoHoiAn, images.roomCorridor, images.roomBedroom],
  the_peninsula_bangkok: [images.peninsulaBangkok, images.roomBedroom, images.roomTerrace],
  siam_kempinski_bangkok: [images.roomBedroom, images.roomTerrace, images.roomCorridor],
  shangri_la_bangkok: [images.roomTerrace, images.roomBedroom, images.roomBalconyCoast],
  amari_bangkok: [images.roomBalconyCoast, images.roomBedroom, images.roomCorridor],
};

const HOTEL_AMENITIES: Record<string, string[]> = {
  phuket_marriott_merlin_beach: ['Beachfront', 'Three pools', 'Spa', 'Free WiFi', 'Fitness Center', 'Kids club'],
  outrigger_maldives_maafushivaru: ['Private island', 'House reef', 'Overwater dining', 'Spa', 'Free WiFi'],
  wafaifo_resort_hoi_an: ['Outdoor pool', 'Free WiFi', 'Restaurant', 'Bar', 'Airport shuttle'],
  the_peninsula_bangkok: ['River view', 'Outdoor pool', 'Spa', 'Free WiFi', 'Fitness Center', 'River shuttle'],
  siam_kempinski_bangkok: ['Outdoor pool', 'Family-friendly', 'Breakfast included', 'Free WiFi'],
  shangri_la_bangkok: ['River view', 'Spa access', 'Breakfast included', 'Free WiFi'],
  amari_bangkok: ['Rooftop pool', 'Gym access', 'Breakfast included', 'Free WiFi'],
};

export const mockHotelContent: Record<string, HotelContent> = Object.fromEntries(
  mockHotels.map((hotel) => [
    hotel.id,
    {
      id: hotel.id,
      hid: hotel.hid,
      name: hotel.name,
      starRating: hotel.starRating,
      address: hotel.address,
      description: HOTEL_DESCRIPTIONS[hotel.id],
      images: HOTEL_GALLERIES[hotel.id],
      amenities: HOTEL_AMENITIES[hotel.id],
      latitude: 0,
      longitude: 0,
    } satisfies HotelContent,
  ]),
);

// Order totals are what the customer actually paid: hotel rate for the stay
// plus the booking-value SuiteScanner fee ($39 for these example stays).
export const mockOrders: Order[] = [
  {
    id: 500123,
    hotelName: 'Phuket Marriott Resort & Spa, Merlin Beach',
    hotelThumbnail: images.phuketMarriott,
    checkIn: '2026-10-11',
    checkOut: '2026-10-12',
    roomName: 'Deluxe Room, Pool View',
    guestCount: 2,
    totalPrice: { amount: '169.00', currencyCode: 'USD' },
    paymentType: 'now',
    status: 'upcoming',
    freeCancellationBefore: '2026-08-25T17:00:00Z',
  },
  {
    id: 500098,
    hotelName: 'The Peninsula Bangkok',
    hotelThumbnail: images.peninsulaBangkok,
    checkIn: '2026-06-02',
    checkOut: '2026-06-06',
    roomName: 'Deluxe Room, River View',
    guestCount: 2,
    totalPrice: { amount: '1019.00', currencyCode: 'USD' },
    paymentType: 'deposit',
    status: 'previous',
    freeCancellationBefore: null,
  },
  {
    id: 500071,
    hotelName: 'Wafaifo Resort, Hoi An',
    hotelThumbnail: images.wafaifoHoiAn,
    checkIn: '2026-05-14',
    checkOut: '2026-05-16',
    roomName: 'Premier Double Room',
    guestCount: 1,
    totalPrice: { amount: '211.00', currencyCode: 'USD' },
    paymentType: 'now',
    status: 'cancelled',
    freeCancellationBefore: null,
  },
];

export const mockUser: UserProfile = {
  firstName: 'Alex',
  lastName: 'Morgan',
  email: 'alex.morgan@example.com',
  phone: '+1 555 019 4432',
};
